<template><section><h1>Container</h1>
<blockquote>
<p>用于将元素的宽度固定到当前断点的组件。</p>
</blockquote>
<table>
<thead>
<tr>
<th>class</th>
<th>断点</th>
<th>属性</th>
</tr>
</thead>
<tbody>
<tr>
<td>.container</td>
<td>空</td>
<td>width: 100%;</td>
</tr>
<tr>
<td></td>
<td>sm (640px)</td>
<td>max-width: 640px;</td>
</tr>
<tr>
<td></td>
<td>md (768px)</td>
<td>max-width: 768px;</td>
</tr>
<tr>
<td></td>
<td>lg (1024px)</td>
<td>max-width: 1024px;</td>
</tr>
<tr>
<td></td>
<td>xl (1280px)</td>
<td>max-width: 1280px;</td>
</tr>
</tbody>
</table>
<h2>用法</h2>
<p>这个 <code v-pre>.container</code> 类设置元素的 <code v-pre>max-width</code> 以匹配当前断点的 <code v-pre>min-width</code>。适用于固定的屏幕尺寸，而不是适应全屏的页面。</p>
<p>注意，与其他框架中使用的容器不同，<strong>Tailwind 的容器不会自动居中，也没有任何内置的水平填充</strong>。</p>
<p>若要使容器居中，请使用 <code v-pre>.mx-auto</code> 样式:</p>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"container mx-auto"</span>&gt;</span>
  <span class="hljs-comment">&lt;!-- ... --&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p>要添加水平填充，请使用 <code v-pre>.px-{size}</code> utilities 样式:</p>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"container mx-auto px-4"</span>&gt;</span>
  <span class="hljs-comment">&lt;!-- ... --&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p>如果您希望默认情况下使容器居中或包含默认的水平填充，请参见<a href="https://tailwindcss.com/docs/container#customizing">自定义选项</a>。</p>
<h2>响应式</h2>
<p>这个 <code v-pre>container</code> 还包括一些响应变量，比如默认情况下的 <code v-pre>md:container</code>，它允许你在容器中使用断点:</p>
<pre v-pre><code v-pre class="language-html"><span class="hljs-comment">&lt;!-- Full-width fluid until the `lg` breakpoint, then lock to container --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">"lg:container lg:mx-auto"</span>&gt;</span>
  <span class="hljs-comment">&lt;!-- ... --&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<h2>定制</h2>
<h3>默认居中</h3>
<p>默认情况下，将配置文件的 <code v-pre>theme.container</code> 的 <code v-pre>center</code> 选项设置为 <code v-pre>true</code>:</p>
<pre v-pre><code v-pre class="language-js"><span class="hljs-comment">// tailwind.config.js</span>
<span class="hljs-built_in">module</span>.exports = {
  <span class="hljs-attr">theme</span>: {
    <span class="hljs-attr">container</span>: {
      <span class="hljs-attr">center</span>: <span class="hljs-literal">true</span>,
    },
  },
};
</code></pre>
<h3>padding</h3>
<p>默认情况下要添加 padding，请在配置文件的 <code v-pre>theme.container</code> 部分使用 <code v-pre>padding</code> 选项指定需要的填充值:</p>
<pre v-pre><code v-pre class="language-js"><span class="hljs-comment">// tailwind.config.js</span>
<span class="hljs-built_in">module</span>.exports = {
  <span class="hljs-attr">theme</span>: {
    <span class="hljs-attr">container</span>: {
      <span class="hljs-attr">padding</span>: <span class="hljs-string">"2rem"</span>,
    },
  },
};
</code></pre>
<p>如果要为每个断点指定不同的填充值，请使用对象提供 <code v-pre>default</code> 值和任何特定于断点的值:</p>
<pre v-pre><code v-pre class="language-js"><span class="hljs-comment">// tailwind.config.js</span>
<span class="hljs-built_in">module</span>.exports = {
  <span class="hljs-attr">theme</span>: {
    <span class="hljs-attr">container</span>: {
      <span class="hljs-attr">padding</span>: {
        <span class="hljs-attr">default</span>: <span class="hljs-string">"1rem"</span>,
        <span class="hljs-attr">sm</span>: <span class="hljs-string">"2rem"</span>,
        <span class="hljs-attr">lg</span>: <span class="hljs-string">"4rem"</span>,
        <span class="hljs-attr">xl</span>: <span class="hljs-string">"5rem"</span>,
      },
    },
  },
};
</code></pre>
<h3>禁用响应式变量</h3>
<p>如果你想禁用响应式变量，你可以通过在 <code v-pre>tailwind.config.js</code> 文件的变量部分设置 <code v-pre>container</code> 空数组来实现:</p>
<pre v-pre><code v-pre class="language-js"><span class="hljs-comment">// tailwind.config.js</span>
  <span class="hljs-built_in">module</span>.exports = {
    <span class="hljs-attr">variants</span>: {
      <span class="hljs-comment">// ...</span>
+     container: [],
    }
  }
</code></pre>
<h3>完全禁用</h3>
<p>如果你不打算使用 <code v-pre>.container</code> 你可以在配置文件的 <code v-pre>correplugins</code> 部分设置 <code v-pre>container</code> 属性为 <code v-pre>false</code> 来完全禁用它:</p>
<pre v-pre><code v-pre class="language-js">  <span class="hljs-comment">// tailwind.config.js</span>
  <span class="hljs-built_in">module</span>.exports = {
    <span class="hljs-attr">corePlugins</span>: {
      <span class="hljs-comment">// ...</span>
+     container: <span class="hljs-literal">false</span>,
    }
  }
</code></pre>
</section></template>

